import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IDecaDocument, IDecaDocumentSegment, isDecaServicePriorToTimestamp, UUID } from '@okcargo-apps/shared-domain';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaEditReasonFormModel, DecaEditWeightForm } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-net-weight-modal',
  templateUrl: './deca-edit-net-weight-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonInput,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    DecaEditReasonComponent,
    DecaListItemComponent,
  ],
})
export class DecaEditNetWeightModalComponent implements OnInit {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;
  public readonly segment!: IDecaDocumentSegment;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);

  // Usamos control con valor inicial null para evitar el "0" por defecto si estaba vacío
  protected readonly form: FormGroup<DecaEditWeightForm> = this.fb.group<DecaEditWeightForm>({
    netWeight: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
    changeReason: this.fb.group<DecaEditReasonFormModel>({ reason: this.fb.nonNullable.control('') }),
  });

  protected get isServiceStarted(): boolean {
    return isDecaServicePriorToTimestamp(this.document, new Date());
  }

  private requireChangeReasonWhenServiceStarted(): void {
    if (!this.isServiceStarted) return;

    const reason = this.form.controls.changeReason.controls.reason;
    reason.addValidators(Validators.required);
    reason.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.requireChangeReasonWhenServiceStarted();
    this.form.patchValue({
      netWeight: this.segment.netWeight ?? null,
    });
  }

  protected get errorText(): string {
    const control = this.form.controls.netWeight;
    if (control.hasError('required')) return 'Debe introducir el peso neto en kg para poder enviarlo';
    if (control.hasError('min')) return 'El peso debe ser mayor a 0 kg';
    return 'Dato no válido';
  }

  protected async saveChanges(): Promise<void> {
    if (this.form.invalid) {
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const updateSegment: IDecaDocumentSegment = {
        ...this.segment,
        netWeight: this.form.controls.netWeight.value as number,
      };

      const updatedDocument: IDecaDocument = {
        ...this.document,
        segments: this.document.segments.map((segment) => (segment.id === updateSegment.id ? updateSegment : segment)),
      };

      await this.decaMutationsService.updateNetWeight(
        updatedDocument,
        this.segment.id,
        this.form.controls.changeReason.controls.reason.value.trim(),
      );

      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: ['Ha enviado un nuevo dato de peso de la mercancía a este documento'],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error updating net weight', error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
